/**
 * Composant du tableau des combinaisons - Calculatrice AEP
 */

import React from 'react';
import { Download, Search, ChevronDown, ChevronUp, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Card, CardContent, CardHeader } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { formatNumber, formatPrice } from '../../lib/aep-calculations';

export const CombinationsTable = ({
  showTable,
  setShowTable,
  tableauTrie,
  searchTerm,
  setSearchTerm,
  sortConfig,
  handleSort,
  onExportCSV
}) => {
  // Icône de tri
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return <ArrowUpDown className="h-3 w-3" />;
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="h-3 w-3" /> 
      : <ArrowDown className="h-3 w-3" />;
  };

  return (
    <Collapsible open={showTable} onOpenChange={setShowTable}>
      <Card className="border border-[#CBD5E1] rounded-sm shadow-none">
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CollapsibleTrigger asChild>
              <Button 
                variant="ghost" 
                className="p-0 h-auto font-semibold text-lg text-[#0F172A] hover:bg-transparent justify-start"
                data-testid="toggle-table-button"
              >
                {showTable ? (
                  <ChevronUp className="h-5 w-5 mr-2 text-[#1E3A8A]" />
                ) : (
                  <ChevronDown className="h-5 w-5 mr-2 text-[#1E3A8A]" />
                )}
                Tableau des combinaisons
                <Badge variant="secondary" className="ml-2 font-mono-data text-xs">
                  {tableauTrie.length} lignes
                </Badge>
              </Button>
            </CollapsibleTrigger>
            
            <div className="flex flex-wrap items-center gap-3">
              {/* Recherche */}
              <div className="relative flex-1 min-w-[200px] sm:flex-initial">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#475569]" />
                <Input
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 border-[#CBD5E1] rounded-sm text-sm"
                  data-testid="search-input"
                />
              </div>
              
              {/* Export CSV */}
              <Button
                onClick={onExportCSV}
                className="bg-[#1E3A8A] hover:bg-[#172554] text-white rounded-sm uppercase text-xs tracking-wide"
                data-testid="export-csv-button"
              >
                <Download className="h-4 w-4 mr-2" />
                Exporter CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        
        <CollapsibleContent>
          <CardContent className="pt-0">
            <div className="overflow-x-auto -mx-6 px-6">
              <Table data-testid="combinations-table">
                <TableHeader className="sticky top-0 bg-white z-10">
                  <TableRow className="border-b-2 border-[#CBD5E1]">
                    <TableHead 
                      className="cursor-pointer hover:bg-[#F1F5F9] transition-colors text-xs font-semibold text-[#0F172A]"
                      onClick={() => handleSort('pressionAmont')}
                    >
                      <div className="flex items-center gap-1">
                        P. amont
                        {getSortIcon('pressionAmont')}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-amber-600">P. aval min</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-[#F1F5F9] transition-colors text-xs font-semibold text-[#059669]"
                      onClick={() => handleSort('pressionExploitable')}
                    >
                      <div className="flex items-center gap-1">
                        P. exploit.
                        {getSortIcon('pressionExploitable')}
                      </div>
                    </TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-[#F1F5F9] transition-colors text-xs font-semibold text-[#0F172A]"
                      onClick={() => handleSort('debit')}
                    >
                      <div className="flex items-center gap-1">
                        Débit
                        {getSortIcon('debit')}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#0F172A]">Unité</TableHead>
                    <TableHead className="text-xs font-semibold text-[#0F172A]">Rend.</TableHead>
                    <TableHead className="text-xs font-semibold text-[#0F172A] text-right">P. théo. (kW)</TableHead>
                    <TableHead className="text-xs font-semibold text-[#0F172A] text-right">P. utile (kW)</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-[#F1F5F9] transition-colors text-xs font-semibold text-[#0F172A] text-right"
                      onClick={() => handleSort('energieUtileKWh')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        E. utile (kWh)
                        {getSortIcon('energieUtileKWh')}
                      </div>
                    </TableHead>
                    <TableHead className="text-xs font-semibold text-[#0F172A] text-right">Vente EDF</TableHead>
                    <TableHead className="text-xs font-semibold text-[#059669] text-right">Autocons.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tableauTrie.map((row, index) => (
                    <TableRow 
                      key={`${row.pressionAmont}-${row.debit}-${row.unite}`}
                      className={`
                        transition-colors duration-150
                        ${row.isSelected ? 'aep-row-highlight' : ''}
                        ${row.isNonExploitable ? 'bg-red-50 opacity-60' : ''}
                        ${!row.isNonExploitable && row.unite === 'lmin' ? 'aep-table-row-lmin' : ''}
                        ${!row.isNonExploitable && row.unite === 'm3h' ? 'aep-table-row-m3h' : ''}
                        hover:bg-[#DBEAFE]/50
                      `}
                      data-testid={`table-row-${index}`}
                    >
                      <TableCell className="font-mono-data text-sm">{row.pressionAmont} bar</TableCell>
                      <TableCell className="font-mono-data text-sm text-amber-600">{row.pressionAval} bar</TableCell>
                      <TableCell className={`font-mono-data text-sm font-medium ${row.pressionExploitable > 0 ? 'text-[#059669]' : 'text-red-500'}`}>
                        {row.pressionExploitable} bar
                      </TableCell>
                      <TableCell className="font-mono-data text-sm">{row.debit}</TableCell>
                      <TableCell>
                        <Badge 
                          variant={row.unite === 'lmin' ? 'outline' : 'secondary'}
                          className="font-mono-data text-xs rounded-sm"
                        >
                          {row.uniteLabel}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-mono-data text-sm">{row.rendement}%</TableCell>
                      <TableCell className="font-mono-data text-sm text-right">
                        {formatNumber(row.puissanceTheoriqueKW, 4)}
                      </TableCell>
                      <TableCell className="font-mono-data text-sm text-right text-[#059669] font-medium">
                        {formatNumber(row.puissanceUtileKW, 4)}
                      </TableCell>
                      <TableCell className="font-mono-data text-sm text-right text-[#059669] font-medium">
                        {formatNumber(row.energieUtileKWh)}
                      </TableCell>
                      <TableCell className="font-mono-data text-sm text-right">
                        {formatPrice(row.revenusVenteEDF)}
                      </TableCell>
                      <TableCell className="font-mono-data text-sm text-right text-[#059669] font-medium">
                        {formatPrice(row.revenusAutoconsommation)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            
            {/* Légende */}
            <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t border-[#CBD5E1] text-xs text-[#475569]">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#F8FAFC] border border-[#CBD5E1] rounded-sm"></div>
                <span>L/min</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#F1F5F9] border border-[#CBD5E1] rounded-sm"></div>
                <span>m³/h</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-[#DBEAFE] border-l-2 border-l-[#1E3A8A] border border-[#CBD5E1] rounded-sm"></div>
                <span>Combinaison sélectionnée</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-50 border border-red-200 rounded-sm"></div>
                <span>Pression non exploitable (≤3 bars)</span>
              </div>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export default CombinationsTable;
